import { useAuth } from '@/providers/Auth';
import { ENDPOINT } from '@/services/endpoint';
import { KpiAppUsageService } from '@/services/kpi';
import { IResponseError } from '@/types/common';
import { KpiAppUsageSource } from '@/types/response';
import { handleErrorCommon, notificationMessage, NOTIFY_STATUS } from '@/utils';
import { ADMIN_ONLY, hasAnyPermission } from '@/utils/permission';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  ConfigProvider,
  Empty,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useMutation, useQuery } from 'react-query';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { SURFACE_THEME } from '../Dashboard';
import { SERIES_COLORS, formatNumber } from '../Dashboard/KpiChart';
import {
  IUsageRow,
  categorySummaries,
  comparisonOf,
  formatDuration,
  monthlyTrend,
  periodKey,
  periodsOf,
  toPeriod,
  usageRows,
} from './appUsage';
import { FunnelView } from './FunnelView';
import { ImportModal } from './ImportModal';
import { labelOf } from './labels';

const { Title, Text } = Typography;

interface IProps {
  title?: string;
}

const SOURCE_OPTIONS: { label: string; value: KpiAppUsageSource }[] = [
  { label: '画面', value: 'ga4' },
  { label: '機能', value: 'appsflyer' },
  { label: '新規ファネル', value: 'appsflyer_cohort' },
];

/** 推移グラフは系列色の数（3）まで */
const MAX_TREND = SERIES_COLORS.length;

const ChangeText: React.FC<{ pct: number | null }> = ({ pct }) => {
  if (pct === null) return <Text type='secondary'>—</Text>;
  const color =
    pct > 0 ? 'text-[#1baf7a]' : pct < 0 ? 'text-[#d64545]' : 'text-fg-muted';

  return (
    <span className={color}>
      {pct > 0 ? '▲' : pct < 0 ? '▼' : '±'}
      {Math.abs(pct)}%
    </span>
  );
};

/**
 * アプリの画面（GA4）・機能（AppsFlyer）ごとの利用状況。
 * 月2回、管理者が CSV を取り込む（15日ごろに当月前半の速報、月初に前月の確定値）。
 */
export const AppUsage: React.FC<IProps> = ({ title }) => {
  const { currentUser } = useAuth();
  const canEdit = hasAnyPermission(currentUser, ADMIN_ONLY);

  const [source, setSource] = useState<KpiAppUsageSource>('ga4');
  const [periodKeyState, setPeriodKey] = useState<string | null>(null);
  const [trendNames, setTrendNames] = useState<string[] | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const query = useQuery([ENDPOINT.KPI_APP_USAGE.GET_LIST], () =>
    KpiAppUsageService.getList(),
  );
  const all = useMemo(() => query.data?.data ?? [], [query.data]);

  const periods = useMemo(() => periodsOf(all, source), [all, source]);
  const period =
    periods.find((p) => p.key === periodKeyState) ?? periods[0] ?? null;

  // 取得元を切り替えたら、その取得元の最新期間と推移の既定（上位）に戻す
  useEffect(() => {
    setPeriodKey(null);
    setTrendNames(null);
  }, [source]);

  const rows = useMemo(
    () => (period ? usageRows(all, period) : []),
    [all, period],
  );
  const summaries = useMemo(
    () => (period ? categorySummaries(all, period, rows) : []),
    [all, period, rows],
  );
  const comparison = period ? comparisonOf(period) : null;
  const comparisonLabel = comparison
    ? toPeriod(source, comparison.periodStart, comparison.periodEnd).label
    : null;
  const hasComparison =
    !!comparison &&
    all.some((r) => periodKey(r) === periodKey({ source, ...comparison }));

  const selectedTrend =
    trendNames ?? rows.slice(0, MAX_TREND).map((r) => r.name);
  const trend = useMemo(
    () => monthlyTrend(all, source, selectedTrend),
    [all, source, selectedTrend],
  );
  const nameOptions = useMemo(() => {
    const names = Array.from(
      new Set(all.filter((r) => r.source === source).map((r) => r.name)),
    );

    return names
      .map((name) => ({ value: name, label: labelOf(source, name).label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ja'));
  }, [all, source]);

  const deleteMutation = useMutation(
    () =>
      KpiAppUsageService.deletePeriod({
        source,
        periodStart: period!.periodStart,
        periodEnd: period!.periodEnd,
      }),
    {
      onSuccess: () => {
        notificationMessage({
          message: '期間を削除しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        setPeriodKey(null);
        query.refetch();
      },
      onError: (e: IResponseError) =>
        handleErrorCommon(
          e,
          (field: string, { message }: { message: string }) =>
            notificationMessage({
              message: `削除できませんでした（${field}: ${message}）`,
              type: NOTIFY_STATUS.ERROR,
            }),
        ),
    },
  );

  const isGa4 = source === 'ga4';
  const isCohort = source === 'appsflyer_cohort';
  const columns: TableColumnsType<IUsageRow> = [
    {
      title: isGa4 ? '画面' : '機能',
      key: 'label',
      render: (_, r) => (
        <div className='flex flex-col'>
          <span className='text-fg'>{r.label}</span>
          {r.label !== r.name && (
            <span className='text-fg-muted text-[11px]'>{r.name}</span>
          )}
        </div>
      ),
    },
    {
      title: '分類',
      dataIndex: 'category',
      width: 140,
      filters: summaries.map((s) => ({ text: s.category, value: s.category })),
      onFilter: (value, r) => r.category === value,
      render: (c: string) => <Tag>{c}</Tag>,
    },
    {
      title: 'ユーザー',
      dataIndex: 'users',
      align: 'right',
      width: 100,
      sorter: (a, b) => a.users - b.users,
      defaultSortOrder: 'descend',
      render: (v: number) => formatNumber(v),
    },
    {
      title: comparisonLabel ? `前月比` : '比較',
      key: 'change',
      align: 'right',
      width: 90,
      sorter: (a, b) =>
        (a.usersChangePct ?? -Infinity) - (b.usersChangePct ?? -Infinity),
      render: (_, r) => <ChangeText pct={r.usersChangePct} />,
    },
    {
      title: isGa4 ? '表示回数' : 'イベント数',
      dataIndex: 'count',
      align: 'right',
      width: 110,
      sorter: (a, b) => a.count - b.count,
      render: (v: number) => formatNumber(v),
    },
    {
      title: '1人あたり',
      key: 'perUser',
      align: 'right',
      width: 90,
      render: (_, r) =>
        r.users
          ? formatNumber(Math.round((r.count / r.users) * 10) / 10, '回')
          : '—',
    },
    ...(isGa4
      ? [
          {
            title: '平均滞在',
            dataIndex: 'engagementSeconds',
            align: 'right' as const,
            width: 100,
            sorter: (a: IUsageRow, b: IUsageRow) =>
              (a.engagementSeconds ?? 0) - (b.engagementSeconds ?? 0),
            render: (v: number | null) => formatDuration(v),
          },
        ]
      : []),
  ];

  return (
    <ConfigProvider theme={SURFACE_THEME}>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <div className='flex flex-col gap-xl pb-xl'>
        <div className='flex flex-wrap items-end justify-between gap-md'>
          <div>
            <Title level={4} className='!mb-xs'>
              アプリ利用状況
            </Title>
            <Text type='secondary' className='text-[12px]'>
              画面は GA4（Firebase）、機能は AppsFlyer
              の集計を月2回取り込んで表示します（15日ごろに当月前半の速報、月初に前月の確定値）。
              新規ファネルは AppsFlyer のインストール週ごと（7日分そろった週）
            </Text>
          </div>
          <Space wrap>
            <Segmented
              options={SOURCE_OPTIONS}
              value={source}
              onChange={(v) => setSource(v as KpiAppUsageSource)}
            />
            {periods.length > 0 && (
              <Select
                className='min-w-[220px]'
                value={period?.key}
                onChange={setPeriodKey}
                options={periods.map((p) => ({ value: p.key, label: p.label }))}
              />
            )}
            {canEdit && (
              <Button
                icon={<UploadOutlined />}
                onClick={() => setImportOpen(true)}
              >
                CSV 取り込み
              </Button>
            )}
            {canEdit && period && (
              <Popconfirm
                title='この期間を削除しますか？'
                description={period.label}
                okText='削除'
                cancelText='やめる'
                onConfirm={() => deleteMutation.mutate()}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleteMutation.isLoading}
                >
                  期間を削除
                </Button>
              </Popconfirm>
            )}
          </Space>
        </div>

        {query.isLoading && (
          <div className='flex justify-center py-3xl'>
            <Spin />
          </div>
        )}

        {query.isError && (
          <Alert
            type='error'
            showIcon
            message='取得できませんでした'
            description={
              <Button size='small' onClick={() => query.refetch()}>
                再読み込み
              </Button>
            }
          />
        )}

        {query.isSuccess && !period && (
          <Card>
            <Empty
              description={
                isGa4
                  ? 'まだ取り込みがありません。GA4 の「レポート → エンゲージメント → ページとスクリーン」で期間を指定して CSV をダウンロードし、取り込んでください。'
                  : isCohort
                    ? 'まだ取り込みがありません。AppsFlyer のインストール週ごとの集計を取り込み用の CSV にして取り込んでください。'
                    : 'まだ取り込みがありません。AppsFlyer のイベント集計を取り込み用の CSV にして取り込んでください。'
              }
            />
          </Card>
        )}

        {period && isCohort && <FunnelView all={all} period={period} />}

        {period && !isCohort && (
          <>
            <section className='flex flex-col gap-md'>
              <div className='flex items-baseline gap-sm'>
                <Title level={5} className='!mb-0'>
                  分類別
                </Title>
                <Text type='secondary' className='text-[12px]'>
                  ユーザー数は分類内でいちばん多い{isGa4 ? '画面' : '機能'}
                  の値（
                  {isGa4 ? '画面' : '機能'}ごとの人数は重なるので足さない）。
                  {isGa4 ? '表示回数' : 'イベント数'}は合計。
                  {comparisonLabel &&
                    (hasComparison
                      ? `前月比は ${comparisonLabel} と比較`
                      : `比較する ${comparisonLabel} は未取り込み`)}
                </Text>
              </div>
              <div className='grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-md'>
                {summaries.map((s) => (
                  <Card key={s.category} size='small'>
                    <div className='text-fg-muted text-[12px]'>
                      {s.category}
                    </div>
                    <div className='flex items-baseline gap-sm'>
                      <span className='text-fg text-[20px] font-semibold leading-tight'>
                        {formatNumber(s.users, '人')}
                      </span>
                      <span className='text-[12px]'>
                        <ChangeText pct={s.usersChangePct} />
                      </span>
                    </div>
                    <div
                      className='text-fg-muted text-[11px] truncate'
                      title={s.topLabel}
                    >
                      最多: {s.topLabel}
                    </div>
                    <div className='text-fg-muted text-[11px]'>
                      {isGa4 ? '表示' : 'イベント'}{' '}
                      {formatNumber(s.count, '回')}
                    </div>
                  </Card>
                ))}
              </div>
            </section>

            <Card
              size='small'
              title={`${isGa4 ? '画面' : '機能'}別（${period.label}）`}
            >
              <Table<IUsageRow>
                // 取得元で分類の選択肢が変わるので、切り替えたら絞り込み・並べ替えを捨てて作り直す
                key={source}
                size='small'
                rowKey='name'
                pagination={false}
                scroll={{ x: 760, y: 560 }}
                dataSource={rows}
                columns={columns}
              />
            </Card>

            <Card
              size='small'
              title='月ごとのユーザー数（確定値）'
              extra={
                <Select
                  mode='multiple'
                  className='min-w-[320px] max-w-[560px]'
                  value={selectedTrend}
                  onChange={(v: string[]) =>
                    setTrendNames(v.slice(0, MAX_TREND))
                  }
                  options={nameOptions}
                  optionFilterProp='label'
                  maxTagCount='responsive'
                  placeholder={`${MAX_TREND}つまで選べます`}
                />
              }
            >
              {trend.length === 0 ? (
                <Empty description='確定値（月全体）の取り込みがまだありません' />
              ) : (
                <ResponsiveContainer width='100%' height={280}>
                  <LineChart
                    data={trend}
                    margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
                  >
                    <CartesianGrid stroke='#E5E5E5' vertical={false} />
                    <XAxis
                      dataKey='month'
                      tick={{ fontSize: 12, fill: '#666666' }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: '#666666' }}
                      tickFormatter={(v) => formatNumber(v)}
                      width={56}
                    />
                    <Tooltip
                      formatter={(v: number, name: string) => [
                        formatNumber(v, '人'),
                        labelOf(source, name).label,
                      ]}
                    />
                    <Legend
                      formatter={(name: string) => labelOf(source, name).label}
                    />
                    {selectedTrend.map((name, i) => (
                      <Line
                        key={name}
                        type='monotone'
                        dataKey={name}
                        stroke={SERIES_COLORS[i]}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls={false}
                        isAnimationActive={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card>
          </>
        )}
      </div>

      {canEdit && (
        <ImportModal
          open={importOpen}
          existingKeys={new Set(all.map((r) => periodKey(r)))}
          onClose={() => setImportOpen(false)}
          onImported={(parsed) => {
            setImportOpen(false);
            setSource(parsed.source);
            query.refetch().then(() => setPeriodKey(periodKey(parsed)));
          }}
          onPartiallyImported={() => query.refetch()}
        />
      )}
    </ConfigProvider>
  );
};
